import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    Users, MessageSquare, Ticket, ListChecks, Calendar,
    FolderArchive, Wallet, Settings, LogOut, Video, User, Shield, Menu, LayoutGrid,
} from "lucide-react";
import { Brand } from "../components/Logo";
import { useAuth } from "../contexts/AuthContext";
import { CountersProvider, useCounters } from "../contexts/CountersContext";
import StaffDashboard from "./dashboard/StaffDashboard";
import MaraFooter from "../components/MaraFooter";

const NAV_ITEMS = [
    { key: "home",       label: "Oggi",             Icon: LayoutGrid,    to: "/app", enabled: true, end: true },
    { key: "clients",    label: "Società clienti", Icon: Users,         to: "/app/clienti", enabled: true },
    { key: "comms",      label: "Comunicazioni",   Icon: MessageSquare, to: "/app/comunicazioni", enabled: true },
    { key: "tickets",    label: "Ticket",          Icon: Ticket,        to: "/app/ticket", enabled: true },
    { key: "deadlines",  label: "Scadenze",        Icon: Calendar,      to: "/app/scadenze", enabled: true },
    { key: "meetings",   label: "Riunioni",        Icon: Video,         to: "/app/riunioni", enabled: true },
    { key: "docs",       label: "Documenti",       Icon: FolderArchive, to: "/app/documenti", enabled: true },
    { key: "checklist",  label: "Checklist",       Icon: ListChecks,    to: "/app/checklist", enabled: true },
    { key: "expenses",   label: "Spese e costi",   Icon: Wallet,        to: "/app/spese", enabled: true },
    { key: "profile",    label: "Il mio profilo",  Icon: User,          to: "/app/profilo", enabled: true },
    { key: "settings",   label: "Impostazioni",    Icon: Settings,      to: "/app/impostazioni", enabled: true },
];

const SUPER_ADMIN_ITEMS = [
    { key: "admin-conversations", label: "Amministrazione", Icon: Shield, to: "/app/admin/conversazioni", enabled: true },
];

function SidebarItem({ item, badge, onClick }) {
    const { Icon } = item;
    if (!item.enabled) {
        return (
            <div
                data-testid={`sidebar-item-${item.key}`}
                title="Disponibile nelle prossime fasi"
                className="lt-nav-item lt-nav-item--disabled"
            >
                <Icon size={16} strokeWidth={1.75} />
                <span>{item.label}</span>
            </div>
        );
    }
    return (
        <NavLink
            to={item.to}
            data-testid={`sidebar-item-${item.key}`}
            end={item.end || false}
            onClick={onClick}
            className={({ isActive }) => "lt-nav-item " + (isActive ? "is-active" : "")}
        >
            <Icon size={16} strokeWidth={1.75} />
            <span>{item.label}</span>
            {badge > 0 && <span className="lt-badge-count" data-testid={`sidebar-badge-${item.key}`}>{badge}</span>}
        </NavLink>
    );
}

function AppShellInner() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { counters } = useCounters();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const handleLogout = async () => { await logout(); navigate("/login"); };
    const closeDrawer = () => setDrawerOpen(false);

    // FIX RIUNIONI 2026-11-02: durante una call Daily (/app/riunioni/:id/join)
    // nascondi sidebar + topbar per fullscreen reale, desktop + mobile.
    const isInCall = /\/riunioni\/[^/]+\/join\b/.test(location.pathname);
    if (isInCall) {
        return (
            <div data-testid="internal-app-shell-incall"
                 style={{ height: "100vh", width: "100vw", overflow: "hidden" }}>
                <Outlet />
            </div>
        );
    }

    return (
        <div className="lt-root-shell" data-testid="internal-app-shell">
            <aside
                className={"lt-sidebar flex flex-col" + (drawerOpen ? " is-open" : "")}
                style={{ padding: "24px 16px" }}
                data-testid="app-sidebar"
                aria-hidden={drawerOpen ? "false" : undefined}
            >
                <div style={{ padding: "0 8px" }}>
                    <Brand logoSize={24} tone="light" />
                </div>
                <div style={{ height: "1px", background: "#E3E5E1", margin: "20px -16px 16px" }} />
                <nav className="flex flex-col gap-1" data-testid="app-sidebar-nav">
                    {NAV_ITEMS.map((it) => {
                        let badge = 0;
                        if (it.key === "tickets") badge = counters?.internal_unread_count || 0;
                        if (it.key === "comms") badge = counters?.total_unread || 0;
                        if (it.key === "deadlines") badge = counters?.scadenze?.scadute || 0;
                        return <SidebarItem key={it.key} item={it} badge={badge} onClick={closeDrawer} />;
                    })}
                    {user?.is_super_admin && (
                        <>
                            <div style={{ height: "1px", background: "#E3E5E1", margin: "16px -16px" }} />
                            {SUPER_ADMIN_ITEMS.map((it) => (
                                <SidebarItem key={it.key} item={it} badge={0} onClick={closeDrawer} />
                            ))}
                        </>
                    )}
                </nav>
            </aside>

            <div
                className={"lt-mobile-overlay" + (drawerOpen ? " is-open" : "")}
                onClick={closeDrawer}
                aria-hidden="true"
                data-testid="app-mobile-overlay"
            />

            <div className="flex flex-col min-w-0">
                <header className="lt-topbar" data-testid="app-topbar">
                    <button
                        type="button"
                        className="lt-mobile-hamburger"
                        onClick={() => setDrawerOpen((v) => !v)}
                        aria-label="Apri menu"
                        aria-expanded={drawerOpen}
                        data-testid="app-hamburger-btn"
                    >
                        <Menu size={20} strokeWidth={2} />
                    </button>
                    <span className="lt-username" data-testid="app-user-name">{user?.name}</span>
                    <div style={{ display: "flex", alignItems: "center", padding: "0 16px" }}>
                        <span className="lt-divider-v" />
                    </div>
                    <button type="button" onClick={handleLogout} data-testid="app-logout-btn" className="lt-btn-ghost">
                        <LogOut size={16} strokeWidth={1.75} />
                        <span>Esci</span>
                    </button>
                </header>

                <main className="flex-1 min-w-0" style={{ background: "#F4F5F2", overflowY: "auto", display: "flex", flexDirection: "column" }} data-testid="app-main">
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <Outlet />
                    </div>
                    <MaraFooter variant="dark" />
                </main>
            </div>
        </div>
    );
}

export default function AppShell() {
    return (
        <CountersProvider>
            <AppShellInner />
        </CountersProvider>
    );
}

export function AppHome() {
    return <StaffDashboard />;
}
